"use client";

import React from "react";
import { X } from "lucide-react";
import type { BaseEntity, SimpleTableConfig } from "../../../types/index";
import { getFilterTypeLabel } from "../utils/operator-labels";
import { FilterChipValue } from "./FilterChipValue";

interface ActiveFilterChipsProps<TData extends BaseEntity = BaseEntity> {
  activeFilters: Array<[string, { type: string; value: any }]>;
  config: SimpleTableConfig<TData>;
  removeAdvancedFilter: (fieldId: string) => void;
}

export function ActiveFilterChips<TData extends BaseEntity = BaseEntity>({
  activeFilters,
  config,
  removeAdvancedFilter,
}: ActiveFilterChipsProps<TData>) {
  if (activeFilters.length === 0) return null;

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">Active filters</span>
        <span className="text-xs text-muted-foreground/70">({activeFilters.length})</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {activeFilters.map(([fieldId, filter]) => {
          const fieldConfig = config.fields[fieldId];
          if (!fieldConfig) return null;
          const typeLabel = getFilterTypeLabel(filter.type);

          return (
            <span
              key={fieldId}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 py-1 pl-2.5 pr-1 text-xs text-foreground"
            >
              <span className="font-semibold text-primary">{fieldConfig.label}</span>
              {typeLabel && <span className="text-muted-foreground">{typeLabel}</span>}
              <FilterChipValue fieldConfig={fieldConfig} filter={filter} />
              <button
                type="button"
                onClick={() => removeAdvancedFilter(fieldId)}
                aria-label={`Remove ${fieldConfig.label} filter`}
                title={`Remove ${fieldConfig.label} filter`}
                className="ml-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-primary transition-colors hover:bg-primary/20 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
}
